import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSidebar } from '../contexts/SidebarContext';
import { useTeam } from '../contexts/TeamContext';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import './Navigation.css';

const Navigation = () => {
  const { currentUser, logout } = useAuth();
  const { toggleSidebar } = useSidebar();
  const { isDark } = useTheme();
  const { currentTeam, availableTeams, setCurrentTeam } = useTeam();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [teamSwitcherOpen, setTeamSwitcherOpen] = useState(false);
  const dropdownRef = useRef(null);
  const teamSwitcherRef = useRef(null);
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Close team switcher when clicking outside
  useEffect(() => {
    if (!teamSwitcherOpen) return;
    const handleClickOutside = (e) => {
      if (teamSwitcherRef.current && !teamSwitcherRef.current.contains(e.target)) {
        setTeamSwitcherOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [teamSwitcherOpen]);

  // Check if we're on a dashboard page
  const isDashboardPage = location.pathname.startsWith('/dashboard');

  // Pages with their own premium navigation - hide main nav on these
  const premiumNavPages = [
    '/',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/pricing',
    '/founding-25',
    '/time-savings',
    '/demo',
    '/beta',
    '/workshop',
    '/workshop/thank-you',
    '/coach-workshop',
    '/coach-workshop/thank-you',
    '/coach-workshop/offer',
    '/features',
    '/features/live-streaming',
    '/features/evergreen',
    '/features/registration',
    '/features/automation',
    '/features/podcasts',
    '/features/repurposing',
    // Short alias for the repurposing page, used as an ad destination.
    // Both paths render the same component, so both must hide the main nav.
    '/repurposing',
    '/for-coaches',
    '/for-churches',
    '/for-educators',
    '/for-speakers',
    '/compare',
    '/compare/streamyard',
    '/compare/everwebinar',
    '/compare/opus-clip',
    '/monetization',
    '/switching'
  ];

  // Hide navigation on pages that have their own premium nav
  const hasPremiumNav = premiumNavPages.includes(location.pathname);

  // Hide navigation on studio/broadcast pages (they have their own full-screen UI)
  const isStudioPage = location.pathname.startsWith('/studio');

  // Hide navigation on live webinar watch pages (full-screen viewer experience)
  const isLiveWebinarPage = location.pathname.match(/^\/webinar\/[^/]+\/live$/);

  // Hide navigation on evergreen webinar watch pages (full-screen viewer experience)
  const isEvergreenWatchPage = location.pathname.match(/^\/webinar\/[^/]+\/watch$/);

  // Hide navigation on embed player pages (minimal external embed)
  const isEmbedPage = location.pathname.startsWith('/embed/');

  // Hide navigation on public branded clip landing pages and recording watch
  // pages (/clip/:uid, /watch/:id) — they carry their own branded chrome, and
  // the global Canira nav would leak onto a white-labeled page.
  const isClipPage = location.pathname.startsWith('/clip/')
    || location.pathname.startsWith('/watch/');

  // Hide navigation on the host's PUBLIC, audience-facing pages — every surface
  // a host shares with THEIR audience: the published landing page, both
  // registration surfaces, the replay and purchase confirmation, their public
  // webinar directory, their podcast page, and their course pages.
  // Same reasoning as the clip pages directly above — these are the host's
  // conversion assets, shown to THEIR audience, and the global Canira nav
  // leaks our chrome onto them. It also hands a visitor who is one click from
  // registering a row of links pointing away from that decision (Explore
  // Webinars, Dashboard, Logout), which is the opposite of what a landing page
  // is for. /webinar/:id/watch and /webinar/:id/live are already hidden above;
  // replay is the same kind of page and was simply missed.
  //   /course/catalog and /podcasts/browse are OURS — Canira's own directories,
  //   so they keep the nav. /course/:courseId shares a pattern with
  //   /course/catalog, hence the explicit exclusion.
  //   /webinars/manage and /webinars/create share the /webinars/:id pattern
  //   but are the host's own pages, so they keep the nav (they lost it when
  //   this list was added).
  const isPublicHostPage = /^\/w\/[^/]+$/.test(location.pathname)
    || /^\/webinars\/(?!(?:manage|create)$)[^/]+$/.test(location.pathname)
    || /^\/webinars\/[^/]+\/register$/.test(location.pathname)
    || /^\/register\/[^/]+$/.test(location.pathname)
    || /^\/webinar\/[^/]+\/replay$/.test(location.pathname)
    || /^\/webinar\/[^/]+\/purchase-success$/.test(location.pathname)
    || /^\/explore\/[^/]+$/.test(location.pathname)
    || /^\/podcast\/[^/]+$/.test(location.pathname)
    || /^\/creator\/[^/]+\/courses$/.test(location.pathname)
    || /^\/course\/(?!catalog$)[^/]+(\/learn)?$/.test(location.pathname);

  // Hide navigation on the founder log / blog (it uses its own
  // home-nav-premium chrome, same pattern as /features and /beta).
  // Regex covers both /blog index and /blog/:slug detail.
  const isBlogPage = location.pathname === '/blog'
    || /^\/blog\/[^/]+$/.test(location.pathname);

  // Hide navigation on the founder/about page (uses home-nav-premium
  // chrome — same pattern as the blog and feature pages). /about is
  // canonical; /founder is an alias.
  const isAboutPage = location.pathname === '/about'
    || location.pathname === '/founder';

  // Hide navigation on the guest's own pages (the invite link's setup page,
  // backstage and the stage). A guest is on camera in someone else's show:
  // the site menu (Explore Webinars, Login, Register) covered the top of the
  // stage and offered links away from it.
  const isGuestPage = /^\/(join|backstage|stage)\/[^/]+$/.test(location.pathname);

  if (isGuestPage || hasPremiumNav || isStudioPage || isLiveWebinarPage || isEvergreenWatchPage || isEmbedPage || isBlogPage || isAboutPage || isClipPage || isPublicHostPage) {
    return null;
  }

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleLogout = () => {
    logout();
    // Close the menu after logout
    setMenuOpen(false);
  };

  return (
    <nav className="navigation" role="navigation" aria-label="Main navigation">
      <div className="nav-container">
        {/* Mobile sidebar toggle button - only shown on dashboard pages */}
        {isDashboardPage && (
          <button className="sidebar-toggle-btn" onClick={toggleSidebar} aria-label="Toggle sidebar menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        )}
        <Link to="/" className="nav-logo">
          <img src={isDark ? "/Canira White Logo updated.png" : "/Canira Black Logo updated .png"} alt="Canira" className="nav-logo-img" />
        </Link>

        {/*
          Team switcher chip — only renders when the user is logged in and
          belongs to ≥1 team. With 1 team it's a static label; with 2+ it
          becomes a dropdown. Switching teams writes to localStorage; the
          axios interceptor reads from there and adds X-Team-Id to all
          subsequent requests, which the backend currentTeam middleware
          uses to scope routes (e.g. GET /api/webinars/my).
        */}
        {currentUser && availableTeams.length > 0 && (
          <div className="team-switcher" ref={teamSwitcherRef}>
            {availableTeams.length === 1 ? (
              <div className="team-switcher-static" title="Active team">
                <span className="team-switcher-label">Team:</span>
                <span className="team-switcher-name">{availableTeams[0].name}</span>
              </div>
            ) : (
              <>
                <button
                  className="team-switcher-toggle"
                  onClick={() => setTeamSwitcherOpen(!teamSwitcherOpen)}
                  aria-expanded={teamSwitcherOpen}
                  aria-haspopup="true"
                  aria-controls="team-switcher-menu"
                >
                  <span className="team-switcher-label">Team:</span>
                  <span className="team-switcher-name">
                    {currentTeam?.name || 'Select team'}
                  </span>
                  <span className="team-switcher-arrow" aria-hidden="true">▼</span>
                </button>
                <ul
                  id="team-switcher-menu"
                  className={`team-switcher-menu ${teamSwitcherOpen ? 'active' : ''}`}
                  role="menu"
                  aria-label="Switch team"
                >
                  {availableTeams.map((team) => (
                    <li key={team.id}>
                      <button
                        className={`team-switcher-item ${currentTeam?.id === team.id ? 'is-active' : ''}`}
                        onClick={() => {
                          setCurrentTeam(team.id);
                          setTeamSwitcherOpen(false);
                        }}
                      >
                        {team.name}
                        {currentTeam?.id === team.id && (
                          <span className="team-switcher-check" aria-hidden="true">✓</span>
                        )}
                      </button>
                    </li>
                  ))}
                  {currentTeam && (
                    <li className="team-switcher-clear-item">
                      <button
                        className="team-switcher-clear"
                        onClick={() => {
                          setCurrentTeam(null);
                          setTeamSwitcherOpen(false);
                        }}
                      >
                        Clear team filter (show all)
                      </button>
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>
        )}

        <div id="nav-menu" className={`nav-menu ${menuOpen ? 'active' : ''}`}>
          <ul className="nav-links">
            <li><Link to="/webinars" onClick={() => setMenuOpen(false)}>Explore Webinars</Link></li>

            {currentUser && (
              <li>
                <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="dashboard-link">
                  Dashboard
                </Link>
              </li>
            )}

            {currentUser && (currentUser.role === 'host' || currentUser.role === 'admin') && (
              <li className="dropdown" ref={dropdownRef}>
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                  aria-controls="webinar-dropdown-menu"
                >
                  Live Sessions <span className="dropdown-arrow" aria-hidden="true">▼</span>
                </button>
                <ul
                  id="webinar-dropdown-menu"
                  className={`dropdown-menu ${dropdownOpen ? 'active' : ''}`}
                  role="menu"
                  aria-label="Session management options"
                >
                  <li>
                    <Link to="/webinars/manage" onClick={() => {
                      setMenuOpen(false);
                      setDropdownOpen(false);
                    }}>
                      My Live Sessions
                    </Link>
                  </li>
                  <li>
                    <Link to="/webinars/create" onClick={() => {
                      setMenuOpen(false);
                      setDropdownOpen(false);
                    }}>
                      Create New Live Session
                    </Link>
                  </li>
                  <li>
                    <Link to="/settings/streaming" onClick={() => {
                      setMenuOpen(false);
                      setDropdownOpen(false);
                    }}>
                      Streaming Settings
                    </Link>
                  </li>
                </ul>
              </li>
            )}
            
            {currentUser ? (
              <>
                <li><Link to="/account" onClick={() => setMenuOpen(false)}>My Account</Link></li>
                <li><button className="logout-btn" onClick={handleLogout}>Logout</button></li>
              </>
            ) : (
              <>
                <li><Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link></li>
                <li><Link to="/register" onClick={() => setMenuOpen(false)}>Register</Link></li>
              </>
            )}
            <li className="theme-toggle-item">
              <ThemeToggle showLabel={false} size="small" />
            </li>
          </ul>
        </div>

        {/* Theme toggle for desktop (outside menu) */}
        <div className="nav-theme-toggle">
          <ThemeToggle showLabel={false} size="small" />
        </div>

        <button
          className="menu-icon"
          onClick={toggleMenu}
          aria-expanded={menuOpen}
          aria-controls="nav-menu"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          <div className={`menu-line ${menuOpen ? 'active' : ''}`} aria-hidden="true"></div>
          <div className={`menu-line ${menuOpen ? 'active' : ''}`} aria-hidden="true"></div>
          <div className={`menu-line ${menuOpen ? 'active' : ''}`} aria-hidden="true"></div>
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
